/**
 * Interactive Clarification prompt component (prompt.md §23, §24, §41).
 *
 * A clarification either carries a structured form (rendered by `FormPrompt`, which
 * protects credential answers) or a list of free-form questions. Both paths require
 * an explicit submit: nothing is sent when the prompt appears.
 */

import { useState } from "react";
import { FormSchema, formRevision as formRevisionOf, type FormDefinition } from "@dayvise/contracts";
import { Badge } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { TextField } from "../primitives/Fields.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { FormPrompt } from "./FormPrompt.tsx";
import type { FormFieldProblem, FormSubmission } from "../logic/forms.ts";

interface LiveClarification {
  readonly clarificationId: string;
  readonly questions?: readonly unknown[];
  readonly form?: unknown;
  readonly formRevision?: string | null;
}

export interface ClarificationPromptProps {
  readonly clarification: LiveClarification;
  readonly onSubmit: (clarificationId: string, answers: Record<string, unknown>) => Promise<void> | void;
  readonly onSubmitForm?: (submission: FormSubmission) => Promise<void> | void;
  readonly onDecline?: () => Promise<void> | void;
  readonly onSnooze?: () => Promise<void> | void;
  readonly onRefresh?: () => Promise<void> | void;
  readonly fieldErrors?: readonly FormFieldProblem[];
  readonly staleFromServer?: boolean;
  readonly draftKey?: string;
  readonly busy?: boolean;
  readonly error?: string | null;
}

interface NormalizedQuestion {
  readonly qid: string;
  readonly id?: string | null;
  readonly question: string;
  readonly choices?: readonly string[] | null;
  readonly choices_offered?: readonly string[] | null;
  readonly multi_select?: boolean;
}

const RECOMMENDED_TAG = "(Recommended)";

export function ClarificationPrompt({
  clarification,
  onSubmit,
  onSubmitForm,
  onDecline,
  onSnooze,
  onRefresh,
  fieldErrors = [],
  staleFromServer = false,
  draftKey = "",
  busy = false,
  error,
}: ClarificationPromptProps) {
  const parsed = clarification.form === undefined ? null : FormSchema.safeParse(clarification.form);
  const parsedForm: FormDefinition | null = parsed !== null && parsed.success ? parsed.data : null;
  const declaredRevision = typeof clarification.formRevision === "string" ? clarification.formRevision : null;
  const [questionsHandled, setQuestionsHandled] = useState(false);
  const rawQuestions = Array.isArray(clarification.questions) ? clarification.questions : [];
  const questions: readonly NormalizedQuestion[] = rawQuestions.map((q, idx) => {
    const item = typeof q === "object" && q !== null ? (q as Record<string, unknown>) : {};
    return {
      qid: String(item.qid ?? `q${idx}`),
      id: item.id ? String(item.id) : null,
      question: String(item.question ?? ""),
      choices: Array.isArray(item.choices) ? item.choices.map(String) : null,
      choices_offered: Array.isArray(item.choices_offered) ? item.choices_offered.map(String) : null,
      multi_select: Boolean(item.multi_select ?? item.multiSelect),
    };
  });

  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string | string[]>>({});
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});

  if (parsedForm !== null) {
    return (
      <FormPrompt
        clarificationId={clarification.clarificationId}
        form={parsedForm}
        formRevision={declaredRevision ?? formRevisionOf(parsedForm, clarification.clarificationId)}
        fieldErrors={fieldErrors}
        busy={busy}
        error={error ?? null}
        staleFromServer={staleFromServer}
        draftKey={draftKey}
        onSubmit={onSubmitForm ?? ((submission) => onSubmit(clarification.clarificationId, submission.answers))}
        {...(onDecline === undefined ? {} : { onDecline })}
        {...(onSnooze === undefined ? {} : { onSnooze })}
        {...(onRefresh === undefined ? {} : { onRefresh })}
      />
    );
  }

  const handleSelectRadio = (key: string, choice: string) => {
    setSelectedAnswers((prev) => ({ ...prev, [key]: choice }));
  };

  const handleToggleCheckbox = (key: string, choice: string) => {
    setSelectedAnswers((prev) => {
      const current = Array.isArray(prev[key]) ? (prev[key] as string[]) : [];
      const updated = current.includes(choice) ? current.filter((c) => c !== choice) : [...current, choice];
      return { ...prev, [key]: updated };
    });
  };

  const handleSubmit = () => {
    const finalAnswers: Record<string, unknown> = {};
    for (const q of questions) {
      const key = q.qid;
      const custom = customAnswers[key]?.trim();
      const selected = selectedAnswers[key];

      if (custom) {
        if (q.multi_select) {
          const arr = Array.isArray(selected) ? [...selected] : [];
          if (!arr.includes(custom)) arr.push(custom);
          finalAnswers[key] = arr;
        } else {
          finalAnswers[key] = custom;
        }
      } else if (selected !== undefined) {
        finalAnswers[key] = selected;
      }
    }
    setQuestionsHandled(true);
    void Promise.resolve(onSubmit(clarification.clarificationId, finalAnswers)).catch(() => {});
  };

  return (
    <Card
      title="Agent needs clarification"
      subtitle="The agent paused to ask for your input before continuing."
    >
      <div style={stackStyle("var(--dayvise-space-md)")}>
        {questions.map((q, idx) => {
          const key = q.qid;
          const displayChoices = q.choices ?? q.choices_offered ?? [];
          const hasChoices = displayChoices.length > 0;
          const currentVal = selectedAnswers[key];

          return (
            <div
              key={key}
              style={{
                ...stackStyle("var(--dayvise-space-xs)"),
                paddingBottom: idx < questions.length - 1 ? "var(--dayvise-space-sm)" : 0,
                borderBottom: idx < questions.length - 1 ? "1px solid var(--dayvise-color-border)" : "none",
              }}
            >
              <div style={rowStyle("var(--dayvise-space-xs)", "space-between")}>
                <strong style={{ fontSize: "var(--dayvise-font-size-sm)" }}>
                  {idx + 1}. {q.question}
                </strong>
                {q.multi_select && <Badge tone="neutral">Multi-select</Badge>}
              </div>

              {hasChoices && (
                <div style={stackStyle("var(--dayvise-space-xxs)")}>
                  {displayChoices.map((choice) => {
                    const isRec = choice.includes(RECOMMENDED_TAG);
                    const cleanLabel = isRec ? choice.replace(RECOMMENDED_TAG, "").trim() : choice;
                    const isChecked = q.multi_select
                      ? Array.isArray(currentVal) && currentVal.includes(choice)
                      : currentVal === choice;

                    return (
                      <label
                        key={choice}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--dayvise-space-xs)",
                          cursor: "pointer",
                          fontSize: "var(--dayvise-font-size-sm)",
                          padding: "var(--dayvise-space-xxs) 0",
                        }}
                      >
                        <input
                          type={q.multi_select ? "checkbox" : "radio"}
                          name={`${clarification.clarificationId}_${key}`}
                          checked={isChecked}
                          onChange={() =>
                            q.multi_select
                              ? handleToggleCheckbox(key, choice)
                              : handleSelectRadio(key, choice)
                          }
                          disabled={busy}
                        />
                        <span>{cleanLabel}</span>
                        {isRec && <Badge tone="accent">Recommended</Badge>}
                      </label>
                    );
                  })}
                </div>
              )}

              <TextField
                label={hasChoices ? "Other / custom response" : "Your response"}
                optional={hasChoices}
                placeholder={hasChoices ? "Or type a custom answer..." : "Type your answer..."}
                value={customAnswers[key] ?? ""}
                onChange={(e) => setCustomAnswers({ ...customAnswers, [key]: e.target.value })}
                disabled={busy}
              />
            </div>
          );
        })}

        {error ? <p role="alert">{error}</p> : null}
        <div style={rowStyle("var(--dayvise-space-xs)", "flex-end")}>
          {onDecline ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => void Promise.resolve(onDecline()).catch(() => undefined)}>Decline</Button> : null}
          {onSnooze ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => void Promise.resolve(onSnooze()).catch(() => undefined)}>Snooze</Button> : null}
          <Button variant="primary" size="sm" loading={busy} disabled={staleFromServer || (questionsHandled && busy)} onClick={handleSubmit}>
            Submit Answers
          </Button>
        </div>

        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
          Your answers will be passed directly to the agent runtime so it can proceed with execution.
        </span>
      </div>
    </Card>
  );
}
